import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { fmtDateTime } from '@/lib/format';
import {
  MessageSquare, Lightbulb, Bug, Heart, Sparkles, Star, Trash2, Check,
  Archive, RefreshCcw, Inbox,
} from 'lucide-react';

const KIND_ICON = { idea: Lightbulb, issue: Bug, praise: Heart, other: Sparkles };
const KIND_TONE = {
  idea: 'text-[hsl(var(--primary))]',
  issue: 'text-[hsl(var(--danger))]',
  praise: 'text-[hsl(var(--success))]',
  other: 'text-[hsl(var(--foreground-muted))]',
};
const STATUS_CHIP = {
  new: 'status-pending',
  reviewed: 'status-in-progress',
  resolved: 'status-done',
  archived: 'status-cancelled',
};
const STATUS_ORDER = ['new', 'reviewed', 'resolved', 'archived'];
const KIND_ORDER = ['idea', 'issue', 'praise', 'other'];

export default function AdminFeedbackPage() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ status: {}, kind: {} });
  const [status, setStatus] = useState('all');
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/feedback/admin', {
        params: {
          ...(status !== 'all' ? { status } : {}),
          ...(kind !== 'all' ? { kind } : {}),
        },
      });
      setRows(r.data.rows || []);
      setCounts({ status: r.data.counts_by_status || {}, kind: r.data.counts_by_kind || {} });
    } catch (e) { toast.error(parseErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status, kind]);

  const shown = useMemo(() => {
    if (!q.trim()) return rows;
    const q_low = q.trim().toLowerCase();
    return rows.filter(r =>
      (r.body || '').toLowerCase().includes(q_low) ||
      (r.user_email || '').toLowerCase().includes(q_low) ||
      (r.user_name || '').toLowerCase().includes(q_low) ||
      (r.page_url || '').toLowerCase().includes(q_low) ||
      (r.page_label || '').toLowerCase().includes(q_low)
    );
  }, [rows, q]);

  const updateStatus = async (id, next) => {
    try {
      await api.patch(`/feedback/admin/${id}/status`, { status: next });
      toast.success(`Marked ${next}.`);
      load();
    } catch (e) { toast.error(parseErr(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this feedback entry? This cannot be undone.')) return;
    try {
      await api.delete(`/feedback/admin/${id}`);
      toast.success('Deleted.');
      load();
    } catch (e) { toast.error(parseErr(e)); }
  };

  const totalCount = Object.values(counts.status || {}).reduce((s, n) => s + n, 0);

  return (
    <AppShell breadcrumbs={[{ label: 'Admin' }, { label: 'Feedback' }]}>
      <div className="p-6 max-w-[1200px] mx-auto space-y-4" data-testid="admin-feedback-page">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <MessageSquare size={20} className="text-[hsl(var(--primary))]" />
            Team feedback
          </h1>
          <p className="text-[13px] text-[hsl(var(--foreground-muted))]">
            Every note the team leaves via the floating <em>Feedback</em> button lands here. Use this to sculpt V03.
          </p>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <KpiTile label="Total" value={totalCount} testId="fb-kpi-total" tone="neutral" />
          {STATUS_ORDER.map(s => (
            <KpiTile key={s} label={s} value={counts.status?.[s] || 0} tone="neutral"
                     testId={`fb-kpi-status-${s}`} />
          ))}
        </div>

        {/* Filters */}
        <div className="card p-3 elev-1 flex items-center gap-2 flex-wrap" data-testid="fb-filters">
          <div className="flex items-center gap-1 flex-wrap">
            <Chip active={status === 'all'} onClick={() => setStatus('all')} testId="fb-filter-status-all">All statuses</Chip>
            {STATUS_ORDER.map(s => (
              <Chip key={s} active={status === s} onClick={() => setStatus(s)}
                    testId={`fb-filter-status-${s}`}>
                {s} <span className="ml-1 text-[10px] opacity-70">{counts.status?.[s] || 0}</span>
              </Chip>
            ))}
          </div>
          <div className="mx-2 h-4 w-px bg-[hsl(var(--border))]" />
          <div className="flex items-center gap-1 flex-wrap">
            <Chip active={kind === 'all'} onClick={() => setKind('all')} testId="fb-filter-kind-all">All kinds</Chip>
            {KIND_ORDER.map(k => {
              const Icon = KIND_ICON[k];
              return (
                <Chip key={k} active={kind === k} onClick={() => setKind(k)}
                      testId={`fb-filter-kind-${k}`}>
                  <Icon size={11} className={`mr-1 ${KIND_TONE[k]}`} />
                  {k} <span className="ml-1 text-[10px] opacity-70">{counts.kind?.[k] || 0}</span>
                </Chip>
              );
            })}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Input value={q} onChange={e => setQ(e.target.value)}
                   placeholder="Search body, sender, page…"
                   className="w-[260px]" data-testid="fb-search" />
            <Button variant="outline" size="sm" onClick={load} data-testid="fb-refresh">
              <RefreshCcw size={13} className="mr-1" />Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="card p-8 text-center text-[hsl(var(--foreground-muted))]" data-testid="fb-loading">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="card p-10 text-center flex flex-col items-center gap-2" data-testid="fb-empty">
            <Inbox size={26} className="text-[hsl(var(--foreground-subtle))]" />
            <div className="text-[14px] font-semibold">No feedback here yet</div>
            <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] max-w-md">
              Once the team starts tapping the floating Feedback button, their notes will land here — grouped by kind and status so you can theme V03 around real usage.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="fb-list">
            {shown.map(r => (
              <FeedbackCard key={r.id} row={r} onStatus={updateStatus} onDelete={remove} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function FeedbackCard({ row, onStatus, onDelete }) {
  const Icon = KIND_ICON[row.kind] || Sparkles;
  return (
    <div className="card p-3 elev-1" data-testid={`fb-card-${row.id}`}>
      <div className="flex items-start gap-3">
        <div className="pt-0.5">
          <div className={`w-8 h-8 rounded-full grid place-items-center bg-[hsl(var(--surface-2))] ${KIND_TONE[row.kind]}`}>
            <Icon size={15} />
          </div>
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold">{row.user_name || row.user_email}</span>
            <span className="text-[11px] font-mono text-[hsl(var(--foreground-subtle))]">{row.user_role}</span>
            <span className={`chip ${STATUS_CHIP[row.status] || ''}`} data-testid={`fb-status-${row.id}`}>{row.status}</span>
            {row.rating != null && (
              <span className="inline-flex items-center gap-0.5" data-testid={`fb-rating-${row.id}`}>
                {[1, 2, 3, 4, 5].map(n => (
                  <Star key={n} size={11}
                        className={n <= row.rating
                          ? 'fill-[hsl(var(--warning))] text-[hsl(var(--warning))]'
                          : 'text-[hsl(var(--foreground-subtle))]'} />
                ))}
              </span>
            )}
            <span className="ml-auto text-[11px] text-[hsl(var(--foreground-subtle))]">
              {fmtDateTime(row.created_at)}
            </span>
          </div>
          <div className="text-[13.5px] leading-relaxed whitespace-pre-wrap break-words">{row.body}</div>
          {row.page_url && (
            <div className="text-[11px] text-[hsl(var(--foreground-subtle))]">
              on <a href={row.page_url} className="underline underline-offset-2 hover:text-[hsl(var(--primary))]">
                {row.page_label || row.page_url}
              </a>
            </div>
          )}
          <div className="flex items-center gap-1 pt-1">
            {row.status !== 'reviewed' && (
              <button onClick={() => onStatus(row.id, 'reviewed')} data-testid={`fb-act-reviewed-${row.id}`}
                      className="text-[11px] px-2 py-1 rounded-md hover:bg-[hsl(var(--surface-2))] flex items-center gap-1">
                <Check size={11} />Mark reviewed
              </button>
            )}
            {row.status !== 'resolved' && (
              <button onClick={() => onStatus(row.id, 'resolved')} data-testid={`fb-act-resolved-${row.id}`}
                      className="text-[11px] px-2 py-1 rounded-md hover:bg-[hsl(var(--surface-2))] flex items-center gap-1 text-[hsl(var(--success))]">
                <Check size={11} />Resolve
              </button>
            )}
            {row.status !== 'archived' && (
              <button onClick={() => onStatus(row.id, 'archived')} data-testid={`fb-act-archived-${row.id}`}
                      className="text-[11px] px-2 py-1 rounded-md hover:bg-[hsl(var(--surface-2))] flex items-center gap-1">
                <Archive size={11} />Archive
              </button>
            )}
            <button onClick={() => onDelete(row.id)} data-testid={`fb-act-delete-${row.id}`}
                    className="text-[11px] px-2 py-1 rounded-md hover:bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] flex items-center gap-1 ml-auto">
              <Trash2 size={11} />Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiTile({ label, value, tone, testId }) {
  return (
    <div className="card p-3 elev-1" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">{label}</div>
      <div className="text-[22px] font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[12px] transition-all
                  ${active
                    ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/.10)] text-[hsl(var(--foreground))]'
                    : 'border-[hsl(var(--border))] text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--surface-2))]'}`}
    >
      {children}
    </button>
  );
}
